/**
 * DescricaoTemplateField
 * -----------------------------------------------------------------------------
 * Campo de Descrição do módulo Financeiro com suporte a VARIÁVEIS dinâmicas.
 *
 * Por que existe:
 *   Descrições fixas ("Rastreamento - PQR3B95") ficam desatualizadas quando
 *   veículos/itens são adicionados ou removidos. Com variáveis, o sistema grava
 *   um TEMPLATE e renderiza a descrição final no momento da emissão de cada
 *   documento (write-time), mantendo o texto sempre coerente.
 *
 * Comportamento:
 *   - Input controlado (mesmo contrato de um <Input value/onChange>)
 *   - Botão "Adicionar variável" abre um Popover agrupado por categoria + busca
 *   - Ao inserir, o token é colocado na posição do cursor
 *   - Quando há tokens, exibe um preview renderizado pelo servidor (mesma RPC
 *     usada na emissão), com debounce de 600ms
 *
 * RELEASE: 2026-08-19 v1.0.0 - Versão inicial
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Braces, Eye, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  FINANCEIRO_VARIABLE_CATEGORIES,
  hasDescricaoVariables,
  useFinanceiroVariablesCatalog,
  usePreviewFinanceiroDescricao,
} from '@/hooks/financial/useFinanceiroDescricaoTemplate';

interface DescricaoTemplateFieldProps {
  /** Template atual (texto com ou sem variáveis) */
  value: string;
  /** Callback de alteração */
  onChange: (value: string) => void;
  /** Placeholder do input */
  placeholder?: string;
  /** Limite de caracteres do template */
  maxLength?: number;
  /** Contexto para o preview (opcionais) */
  customerId?: string | null;
  vehicleIds?: string[] | null;
  cobrancaId?: string | null;
  assinaturaId?: string | null;
  /** Desabilita o campo */
  disabled?: boolean;
}

export function DescricaoTemplateField({
  value,
  onChange,
  placeholder = 'Ex.: Mensalidade {{mes_referencia}} - {{veiculos.placas}}',
  maxLength = 300,
  customerId,
  vehicleIds,
  cobrancaId,
  assinaturaId,
  disabled,
}: DescricaoTemplateFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState<string>('');

  const { data: catalog = [], isLoading } = useFinanceiroVariablesCatalog(pickerOpen);
  const previewMutation = usePreviewFinanceiroDescricao();

  const hasVars = hasDescricaoVariables(value);

  // --- Preview server-side com debounce (só quando existem variáveis) -------
  useEffect(() => {
    if (!hasVars) {
      setPreview('');
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const rendered = await previewMutation.mutateAsync({
          template: value,
          customerId,
          vehicleIds,
          cobrancaId,
          assinaturaId,
        });
        setPreview(rendered);
      } catch {
        setPreview('');
      }
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, hasVars, customerId, cobrancaId, assinaturaId, JSON.stringify(vehicleIds ?? [])]);

  // --- Agrupamento + filtro do catálogo ------------------------------------
  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? catalog.filter(
          (v) =>
            v.token.toLowerCase().includes(term) ||
            (v.label || '').toLowerCase().includes(term) ||
            (v.descricao || '').toLowerCase().includes(term),
        )
      : catalog;

    return FINANCEIRO_VARIABLE_CATEGORIES.map((cat) => ({
      ...cat,
      items: filtered.filter((v) => v.categoria === cat.key),
    })).filter((g) => g.items.length > 0);
  }, [catalog, search]);

  /** Insere o token na posição atual do cursor */
  const insertToken = (token: string) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`.slice(0, maxLength);
    onChange(next);
    setPickerOpen(false);
    setSearch('');
    // Reposiciona o cursor após o token inserido
    requestAnimationFrame(() => {
      el?.focus();
      const pos = Math.min(start + token.length, next.length);
      el?.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          className="flex-1"
        />

        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" disabled={disabled} className="shrink-0 gap-1.5">
              <Braces className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Adicionar variável</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[340px] p-0">
            {/* Busca */}
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar variável..."
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>

            <ScrollArea className="max-h-[320px]">
              <div className="p-1">
                {isLoading && (
                  <p className="px-3 py-6 text-center text-xs text-muted-foreground">Carregando variáveis...</p>
                )}
                {!isLoading && grouped.length === 0 && (
                  <p className="px-3 py-6 text-center text-xs text-muted-foreground">Nenhuma variável encontrada.</p>
                )}
                {grouped.map((group) => (
                  <div key={group.key} className="mb-1">
                    <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </p>
                    {group.items.map((item) => (
                      <button
                        key={item.token}
                        type="button"
                        onClick={() => insertToken(item.token)}
                        className="w-full rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent"
                      >
                        <span className="block text-xs font-medium text-foreground">{item.label}</span>
                        <span className="block font-mono text-[10px] text-muted-foreground">{item.token}</span>
                        {item.exemplo && (
                          <span className="block truncate text-[10px] text-muted-foreground/80">
                            Ex.: {item.exemplo}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>

      {/* Preview renderizado pelo servidor (mesma engine da emissão) */}
      {hasVars && (
        <div className="flex items-start gap-2 rounded-md border border-dashed px-3 py-2">
          <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Prévia
              </span>
              <Badge variant="secondary" className="h-4 px-1.5 text-[9px]">
                atualizada a cada emissão
              </Badge>
            </div>
            <p className="mt-0.5 break-words text-xs text-foreground">
              {previewMutation.isPending && !preview ? 'Gerando prévia...' : preview || '—'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default DescricaoTemplateField;
